import { ref, computed } from 'vue'

export interface AuthUser {
  username: string
}

export function useAuth() {
  const user = useState<AuthUser | null>('auth_user', () => null)
  const isInitialized = useState<boolean>('auth_initialized', () => false)
  const isLoading = ref(false)

  const isAuthenticated = computed(() => !!user.value)

  /**
   * Consulta a sessão ativa no servidor
   */
  async function fetchUser(): Promise<{ authenticated: boolean; user: AuthUser | null }> {
    try {
      isLoading.value = true
      const res = await $fetch<{ authenticated: boolean; user: AuthUser | null }>('/api/auth/me', {
        headers: { Accept: 'application/json' },
      })

      if (res?.authenticated && res.user) {
        user.value = res.user
      } else {
        user.value = null
      }
      isInitialized.value = true
      return { authenticated: Boolean(res?.authenticated), user: user.value }
    } catch {
      user.value = null
      isInitialized.value = true
      return { authenticated: false, user: null }
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Realiza login no sistema
   */
  async function login(username: string, password: string): Promise<{ success: boolean; message?: string }> {
    isLoading.value = true
    try {
      const res = await $fetch<{ success: boolean; message?: string; user?: AuthUser }>('/api/auth/login', {
        method: 'POST',
        body: { username, password },
      })

      if (res?.success && res.user) {
        user.value = res.user
        isInitialized.value = true
        return { success: true }
      }
      return { success: false, message: res?.message || 'Falha ao autenticar' }
    } catch (err: any) {
      const msg = err?.data?.statusMessage || err?.data?.message || err?.message || 'Usuário ou senha incorretos'
      return { success: false, message: msg }
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Encerra a sessão
   */
  async function logout(): Promise<void> {
    isLoading.value = true
    try {
      await $fetch('/api/auth/logout', { method: 'POST' })
    } catch {
      // continua para limpar o estado local
    } finally {
      user.value = null
      isInitialized.value = true
      isLoading.value = false
      navigateTo('/login')
    }
  }

  return {
    user,
    isAuthenticated,
    isInitialized,
    isLoading,
    fetchUser,
    login,
    logout,
  }
}

